/**
 * Venda Direta — assistente de quatro etapas (Fase C do redesign): dados da venda, produtos,
 * valores e pagamento, revisão e finalização. Os dados ficam todos no rascunho da tela
 * (nada se perde ao voltar) e nada é gravado antes de "Finalizar".
 *
 * Finalizar sem prévia manual (opção A aprovada): o clique calcula a prévia na API (mesmas
 * validações), e só com ela válida envia a finalização com o hash dessa prévia — a API
 * continua exigindo o hash e recusando cálculo desatualizado. O identificador do rascunho
 * (clientRequestId) e a chave de idempotência impedem pedido ou nota em dobro em cliques
 * repetidos ou falha de comunicação. A prévia continua disponível para consulta.
 *
 * Depois de finalizar (ajustes pós-homologação): a tela de resultado separa a venda
 * (confirmada) da NF-e (situação acompanhada sozinha) e, na autorização, abre o DANFE numa
 * guia preparada no clique de "Finalizar" e baixa PDF e XML, conforme as preferências. Uma
 * página recarregada durante a finalização oferece "Verificar a venda" (mesmo rascunho:
 * nunca um segundo pedido) e o resultado volta depois de recarregar.
 */
import {
  CARD_BRANDS,
  FISCAL_ENVIRONMENT_LABEL,
  PIX_TXID_RE,
  formatDocument,
  formatMoney,
  saleLineTotal,
  type DirectSalePreview,
  type DirectSaleResult,
  type FiscalEnvironment,
  type SaleDraftItem,
} from "@cafegestor/shared";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useCan } from "@/app/me";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Icon, type IconName } from "@/components/icons";
import { PageHeader } from "@/components/patterns";
import { StepWizard, type WizardStep } from "@/components/StepWizard";
import { Alert, Badge, Button, Field, Input, Select, Table } from "@/components/ui";
import { apiFetch, ApiRequestError, errorMessage, newIdempotencyKey } from "@/lib/api";
import { Checkbox, SaveError } from "../cadastros/widgets";
import { usePaymentMethods } from "../compras/lib";
import { dateBR, qty, useDocAction, useWarehouses } from "../estoque/lib";
import { CalcView, TaxBreakdown } from "../fiscal/CalcView";
import { closeTab, preparePendingTab } from "../fiscal/emission-actions";
import { FISCAL_KEY } from "../fiscal/lib";
import {
  lastResult,
  pendingFinalize,
  readAutoPrefs,
  saveAutoPrefs,
  type AutoPrefs,
  type DirectSaleOutcomeData,
} from "./direct-sale-session";
import { DirectSaleOutcome } from "./DirectSaleOutcome";
import { ORDER_KEY } from "./lib";
import { bodyOf, qtyText, useSaleDraft } from "./sale/draft";
import { QuickItemEntry, type AddOutcome } from "./sale/QuickItemEntry";
import { SaleHeaderFields } from "./sale/SaleHeaderFields";
import { SaleItemsTable } from "./sale/SaleItemsTable";
import { InstallmentsFields, SaleSummary } from "./sale/SaleSummary";

const REQUIRED = [
  ["vendas.pedido.criar", "criar pedidos de venda"],
  ["vendas.pedido.confirmar", "confirmar vendas"],
  ["fiscal.documento.emitir", "emitir documentos fiscais"],
] as const;
const INVALIDATE = [ORDER_KEY, ["estoque"], ["financeiro"], FISCAL_KEY];

export function VendaDiretaPage() {
  const can = useCan();
  const missing = REQUIRED.filter(([code]) => !can(code));
  // cada venda começa um rascunho novo: o identificador protege contra finalizar duas vezes
  const [session, setSession] = useState(newIdempotencyKey);
  // página recarregada: o último resultado volta (situação da nota acompanhada de novo)
  const [result, setResult] = useState<DirectSaleOutcomeData | null>(() => lastResult.get());
  const [prefs, setPrefs] = useState<AutoPrefs>(readAutoPrefs);
  // guia do DANFE preparada no clique de "Finalizar" (só vive nesta página)
  const [pendingTab, setPendingTab] = useState<Window | null>(null);
  const done = (r: DirectSaleResult, customerLabel: string) => {
    const data: DirectSaleOutcomeData = {
      ...r,
      customerLabel,
      finishedAt: new Date().toISOString(),
    };
    pendingFinalize.clear();
    lastResult.set(data);
    setResult(data);
  };
  return (
    <div className="space-y-6">
      <PageHeader
        title="Venda Direta"
        description="Venda da fábrica com nota fiscal numa só operação: gera o pedido já confirmado (estoque e financeiro) e a NF-e ligada a ele. Ao finalizar, a nota é conferida e a emissão começa sozinha; a prévia fiscal fica disponível para consulta."
        breadcrumb={["Vendas", "Venda Direta"]}
      />
      {missing.length > 0 ? (
        <Alert tone="error">
          A Venda Direta exige permissão para {missing.map(([, label]) => label).join(", ")}. Peça
          ao Proprietário para incluir no seu papel.
        </Alert>
      ) : result ? (
        <DirectSaleOutcome
          key={result.documentId}
          result={result}
          prefs={prefs}
          pendingTab={pendingTab}
          onNew={() => {
            lastResult.clear();
            closeTab(pendingTab);
            setPendingTab(null);
            setResult(null);
            setSession(newIdempotencyKey());
          }}
        />
      ) : (
        <>
          <InterruptedFinalize onDone={done} />
          <DirectSaleForm
            key={session}
            clientRequestId={session}
            prefs={prefs}
            onPrefs={(p) => {
              setPrefs(p);
              saveAutoPrefs(p);
            }}
            onTab={(tab) => setPendingTab(tab)}
            onDone={done}
            onCancel={() => setSession(newIdempotencyKey())}
          />
        </>
      )}
    </div>
  );
}

/**
 * A página recarregou (ou caiu) depois do clique em "Finalizar" e antes da resposta: não se
 * sabe se a venda foi gravada. "Verificar a venda" reenvia o MESMO rascunho: a API devolve a
 * venda já gravada (nada se repete) ou conclui a finalização que foi pedida.
 */
function InterruptedFinalize({
  onDone,
}: {
  onDone: (r: DirectSaleResult, customerLabel: string) => void;
}) {
  const [pending, setPending] = useState(() => pendingFinalize.get());
  const check = useDocAction<Record<string, unknown>, DirectSaleResult>({
    path: () => "/vendas/venda-direta",
    invalidate: INVALIDATE,
    onDone: (r) => {
      setPending(null);
      onDone(r, pending?.customerLabel ?? "");
    },
  });
  if (!pending) return null;
  const stale =
    check.error instanceof ApiRequestError && check.error.code === "PREVIA_DESATUALIZADA";
  return (
    <div
      role="alert"
      className="space-y-3 rounded-xl border border-warning/30 bg-warning-soft px-5 py-4 text-warning-strong"
    >
      <p className="font-semibold">A página foi recarregada durante a finalização de uma venda.</p>
      <p className="text-sm">
        {pending.customerLabel ? `Cliente: ${pending.customerLabel}. ` : ""}
        Para não perder nem repetir a venda, verifique: o sistema confere pelo identificador do
        rascunho e mostra a venda já gravada — ou conclui a finalização que você pediu. Nenhum
        pedido ou nota é duplicado.
      </p>
      {stale && (
        <p className="text-sm font-medium">
          A venda não chegou a ser gravada e o cálculo mudou desde então: nada foi gravado. Faça a
          venda de novo abaixo.
        </p>
      )}
      {check.error !== null && !stale && (
        <p className="text-sm font-medium">{errorMessage(check.error)}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {!stale && (
          <Button onClick={() => check.mutate(pending.body)} disabled={check.isPending}>
            {check.isPending ? "Verificando..." : "Verificar a venda"}
          </Button>
        )}
        <Button
          variant="secondary"
          onClick={() => {
            pendingFinalize.clear();
            setPending(null);
          }}
        >
          Dispensar aviso
        </Button>
      </div>
      {!stale && (
        <p className="text-[13px]">
          Ao dispensar, confira em Vendas → Pedidos de venda se a venda foi gravada antes de fazê-la
          de novo.
        </p>
      )}
    </div>
  );
}

type PaymentExtra = { cardBrand: string; authorizationCode: string; nsu: string; pixTxid: string };
const emptyExtra: PaymentExtra = { cardBrand: "", authorizationCode: "", nsu: "", pixTxid: "" };

const STEPS: WizardStep[] = [
  { key: "dados", label: "Dados da venda" },
  { key: "produtos", label: "Produtos da venda" },
  { key: "valores", label: "Valores e pagamento" },
  { key: "revisao", label: "Revisão e finalização" },
];

function DirectSaleForm({
  clientRequestId,
  prefs,
  onPrefs,
  onTab,
  onDone,
  onCancel,
}: {
  clientRequestId: string;
  prefs: AutoPrefs;
  onPrefs: (p: AutoPrefs) => void;
  /** guia do DANFE preparada no clique (null: não pedida ou bloqueada) */
  onTab: (tab: Window | null) => void;
  onDone: (r: DirectSaleResult, customerLabel: string) => void;
  onCancel: () => void;
}) {
  const can = useCan();
  const draft = useSaleDraft(null);
  const { header } = draft.state;
  const methods = usePaymentMethods();
  const method = methods.data?.items.find((m) => m.id === header.paymentMethodId);
  const receivable = method ? method.generatesReceivable : null;
  const card = method?.kind === "cartao_credito" || method?.kind === "cartao_debito";
  const pix = method?.kind === "pix";
  const [step, setStep] = useState(0);
  const [reached, setReached] = useState(0);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [extra, setExtra] = useState<PaymentExtra>(emptyExtra);
  const [releaseDiscount, setReleaseDiscount] = useState(false);
  const [releaseCredit, setReleaseCredit] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const customerRef = useRef<HTMLInputElement | null>(null);
  // trava contra clique duplo entre a prévia automática e o envio
  const finalizing = useRef(false);

  useEffect(() => customerRef.current?.focus(), []);
  useEffect(() => {
    if (!flashKey) return;
    const id = setTimeout(() => setFlashKey(null), 2000);
    return () => clearTimeout(id);
  }, [flashKey]);
  useEffect(() => {
    if (!draft.dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [draft.dirty]);

  // corpo da prévia e do "Finalizar" (mesmos dados: a prévia vale enquanto nada mudar)
  const base = bodyOf(draft, receivable);
  // TxId do PIX: opcional; quando informado, só letras e números (nunca o valor)
  const txid = extra.pixTxid.trim();
  const txidError =
    pix && txid !== "" && !PIX_TXID_RE.test(txid)
      ? "Isto não é um identificador de transação: o TxId tem só letras e números (até 35), como vem no comprovante do PIX. O valor do pagamento é o total da venda — não precisa digitar."
      : null;
  const details = extraDetails(extra, card, pix && txidError === null);
  const body = {
    clientRequestId,
    customerId: base.customerId,
    salespersonId: base.salespersonId,
    warehouseId: base.warehouseId,
    paymentMethodId: base.paymentMethodId,
    orderDate: base.orderDate,
    notes: base.notes,
    items: base.items,
    freight: base.freight,
    other: base.other,
    installments: base.installments,
    payments:
      details && method && draft.preview.total
        ? [{ paymentMethodId: method.id, amount: draft.preview.total, ...details }]
        : [],
    releaseDiscount,
    releaseCredit,
  };
  const signature = JSON.stringify(body);

  const preview = useMutation({
    mutationFn: (b: typeof body) =>
      apiFetch<DirectSalePreview>("/vendas/venda-direta/previa", { method: "POST", body: b }),
  });
  const [seen, setSeen] = useState<{ signature: string; data: DirectSalePreview } | null>(null);
  const fresh = seen !== null && seen.signature === signature ? seen.data : null;
  const finalize = useDocAction<unknown, DirectSaleResult>({
    path: () => "/vendas/venda-direta",
    invalidate: INVALIDATE,
    successMessage: (r) =>
      `Venda ${r.orderNumber} confirmada; a emissão da NF-e nº ${r.fiscalNumber} começou.`,
    onDone: (r) => onDone(r, header.customerLabel),
  });
  const busy = preview.isPending || finalize.isPending;
  const stale =
    finalize.error instanceof ApiRequestError && finalize.error.code === "PREVIA_DESATUALIZADA";

  /** O que falta em cada etapa (só o necessário daquela etapa). */
  function problemAt(n: number): string | null {
    if (n === 0 && !header.customerId) return "Escolha o cliente.";
    if (n === 1) {
      if (draft.state.items.length === 0) return "Inclua ao menos um produto.";
      if (editingKey) return "Termine (Enter) ou cancele (Esc) a edição do item.";
    }
    if (n === 2 && txidError)
      return "Corrija o identificador da transação PIX (ou deixe em branco).";
    return null;
  }
  function goTo(n: number) {
    setFormError(null);
    setStep(n);
    setReached((r) => Math.max(r, n));
  }
  function next() {
    const problem = problemAt(step);
    if (problem) {
      setFormError(problem);
      if (step === 0) customerRef.current?.focus();
      return;
    }
    goTo(Math.min(step + 1, STEPS.length - 1));
  }
  /** Primeira etapa com pendência (para finalizar a partir de qualquer ponto). */
  function firstProblem(): { step: number; message: string } | null {
    for (let n = 0; n < STEPS.length - 1; n++) {
      const message = problemAt(n);
      if (message) return { step: n, message };
    }
    return null;
  }

  function runPreview() {
    const problem = firstProblem();
    if (problem) {
      goTo(problem.step);
      setFormError(problem.message);
      return;
    }
    setFormError(null);
    finalize.reset();
    setShowPreview(true);
    preview.mutate(body, { onSuccess: (data) => setSeen({ signature, data }) });
  }

  /**
   * Finalizar (opção A aprovada): sem prévia manual. Se não há prévia válida para os dados
   * atuais, calcula agora (mesma validação da API) e, se tudo estiver certo, finaliza com o
   * hash dessa prévia. Impedimento: para, mostra os motivos e nada é gravado.
   */
  async function runFinalize() {
    if (busy || finalizing.current) return;
    const problem = firstProblem();
    if (problem) {
      goTo(problem.step);
      setFormError(problem.message);
      return;
    }
    setFormError(null);
    finalizing.current = true;
    // a guia do DANFE só pode ser aberta AGORA, durante o clique; mostra "aguardando" e só
    // recebe o DANFE depois da autorização (fecha se a venda não for finalizada)
    const tab = prefs.openDanfe ? preparePendingTab() : null;
    let sent = false;
    try {
      let data = fresh;
      if (!data) {
        finalize.reset();
        data = await preview.mutateAsync(body);
        setSeen({ signature, data });
      }
      if (!data.canFinalize || !data.fiscal) {
        closeTab(tab);
        setShowPreview(true);
        setFormError(
          data.needsDiscountRelease || data.needsCreditRelease
            ? "A venda precisa de liberação: marque abaixo e clique em Finalizar de novo. Nada foi gravado."
            : "Não é possível finalizar: veja os motivos abaixo. Nada foi gravado.",
        );
        return;
      }
      const final = { ...body, previewHash: data.fiscal.hash };
      // se a página recarregar antes da resposta, a tela oferece "Verificar a venda" com este
      // mesmo corpo (mesmo rascunho: nunca um segundo pedido)
      pendingFinalize.set({ clientRequestId, customerLabel: header.customerLabel, body: final });
      onTab(tab);
      sent = true;
      // erro do servidor: nada do que foi digitado se perde (a tela fica como está)
      finalize.mutate(final, {
        onError: (err) => {
          closeTab(tab);
          onTab(null);
          // o servidor respondeu com erro: nada foi gravado. Sem resposta (rede), a marca fica:
          // clicar de novo (mesmo rascunho e mesma chave) devolve a venda se ela foi gravada
          if (err instanceof ApiRequestError) pendingFinalize.clear();
        },
      });
    } catch {
      // o erro da prévia aparece abaixo (preview.error); nada foi gravado
      setShowPreview(true);
    } finally {
      if (!sent) closeTab(tab);
      finalizing.current = false;
    }
  }

  function onKeyDown(ev: KeyboardEvent<HTMLDivElement>) {
    if (ev.key === "Enter" && (ev.ctrlKey || ev.metaKey)) {
      ev.preventDefault();
      // Ctrl+Enter: finaliza na revisão; nas outras etapas, continua (com a validação)
      if (step === STEPS.length - 1) void runFinalize();
      else next();
    }
  }

  function onAdd(item: SaleDraftItem): AddOutcome {
    const r = draft.addItem(item);
    if (r.outcome !== "repetido") setFlashKey(r.items[r.index]?.key ?? null);
    return r.outcome;
  }

  const editing = draft.state.items.find((i) => i.key === editingKey) ?? null;
  const total = draft.preview.total;
  const methodLabel = method
    ? `${method.name}${method.generatesReceivable ? " (a prazo)" : " (à vista)"}`
    : "A do cadastro do cliente";

  const finalActions = (
    <>
      <Button variant="secondary" onClick={runPreview} disabled={busy}>
        {preview.isPending && !finalizing.current ? "Calculando..." : "Prévia fiscal"}
      </Button>
      <Button size="lg" className="min-w-64" onClick={() => void runFinalize()} disabled={busy}>
        {busy ? (
          <span
            aria-hidden
            className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
          />
        ) : (
          <Icon name="check" size={20} strokeWidth={2.4} />
        )}
        {finalize.isPending
          ? "Finalizando..."
          : preview.isPending
            ? "Conferindo a nota..."
            : "Finalizar venda e emitir NF-e"}
      </Button>
    </>
  );

  return (
    <div onKeyDown={onKeyDown}>
      <StepWizard
        steps={STEPS}
        current={step}
        reached={reached}
        onStep={(n) => {
          // ir adiante pelo indicador também respeita a validação das etapas anteriores
          for (let k = 0; k < n; k++) {
            const problem = problemAt(k);
            if (problem) {
              goTo(k);
              setFormError(problem);
              return;
            }
          }
          goTo(n);
        }}
        onBack={() => goTo(Math.max(step - 1, 0))}
        onNext={next}
        onCancel={() => (draft.dirty ? setConfirmCancel(true) : onCancel())}
        finalActions={finalActions}
        aside={
          <SaleAside
            customer={header.customerLabel}
            salesperson={header.salespersonLabel}
            payment={methodLabel}
            items={draft.state.items.length}
            total={total}
            dirty={draft.dirty}
          />
        }
      >
        {step === 0 && (
          <SaleHeaderFields
            header={header}
            order={null}
            customerRef={customerRef}
            onChange={draft.setHeader}
            noLoad
          />
        )}
        {step === 1 && (
          <>
            <QuickItemEntry
              customerId={header.customerId}
              warehouseId={header.warehouseId}
              editing={editing}
              onAdd={onAdd}
              onUpdate={(key, item) => {
                draft.replaceItem(key, item);
                setFlashKey(key);
              }}
              onCancelEdit={() => setEditingKey(null)}
              onNeedCustomer={() => goTo(0)}
              canSellShort={can("vendas.venda_sem_estoque")}
            />
            <SaleItemsTable
              items={draft.state.items}
              editingKey={editingKey}
              flashKey={flashKey}
              onEdit={setEditingKey}
              onRemove={(key) => {
                draft.removeItem(key);
                if (editingKey === key) setEditingKey(null);
              }}
              totals={draft.preview}
            />
          </>
        )}
        {step === 2 && (
          <>
            <SaleSummary
              freight={draft.state.freight}
              other={draft.state.other}
              preview={draft.preview}
              onFreight={draft.setFreight}
              onOther={draft.setOther}
            />
            <div className="rounded-lg border border-border/70 p-4">
              <p className="text-sm">
                Forma de pagamento: <strong>{methodLabel}</strong>{" "}
                <button
                  type="button"
                  className="ml-1 text-sm font-medium text-primary underline-offset-2 hover:underline"
                  onClick={() => goTo(0)}
                >
                  alterar
                </button>
              </p>
              {receivable === true && (
                <div className="mt-3">
                  <InstallmentsFields
                    rows={draft.state.installments}
                    onChange={draft.setInstallments}
                    total={draft.preview.total}
                    orderDate={header.orderDate}
                    method={method}
                  />
                </div>
              )}
              {(card || pix) && (
                <div className="mt-3 grid gap-3 sm:grid-cols-4">
                  {card && (
                    <>
                      <Field label="Bandeira do cartão">
                        <Select
                          value={extra.cardBrand}
                          onChange={(e) => setExtra({ ...extra, cardBrand: e.target.value })}
                        >
                          <option value="">Não informar</option>
                          {Object.entries(CARD_BRANDS).map(([code, label]) => (
                            <option key={code} value={code}>
                              {label}
                            </option>
                          ))}
                        </Select>
                      </Field>
                      <Field label="Autorização">
                        <Input
                          value={extra.authorizationCode}
                          onChange={(e) =>
                            setExtra({ ...extra, authorizationCode: e.target.value })
                          }
                        />
                      </Field>
                      <Field label="NSU">
                        <Input
                          value={extra.nsu}
                          onChange={(e) => setExtra({ ...extra, nsu: e.target.value })}
                        />
                      </Field>
                    </>
                  )}
                  {pix && (
                    <div className="sm:col-span-2">
                      <Field
                        label="Identificador da transação PIX (TxId) — opcional"
                        hint={`Código do comprovante do PIX (letras e números). Não é o valor: o pagamento é o total da venda${total ? ` (${formatMoney(total)})` : ""}. Em branco, a nota sai só com a forma PIX.`}
                        error={txidError ?? undefined}
                      >
                        <Input
                          value={extra.pixTxid}
                          maxLength={35}
                          inputMode="text"
                          autoComplete="off"
                          aria-invalid={txidError !== null}
                          placeholder="ex.: E2E9f8a7b6c5d4e3f2a1"
                          onChange={(e) => setExtra({ ...extra, pixTxid: e.target.value })}
                        />
                      </Field>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
        {step === 3 && (
          <>
            <SaleReview
              customer={header.customerLabel}
              salesperson={header.salespersonLabel}
              payment={methodLabel}
              warehouseId={header.warehouseId}
              orderDate={header.orderDate}
              items={draft.state.items}
              preview={draft.preview}
              environment={fresh?.fiscal?.environment ?? null}
              prefs={prefs}
              onPrefs={onPrefs}
              onEdit={goTo}
            />
            <SaveError error={preview.error} />
            {showPreview && fresh && (
              <PreviewView
                p={fresh}
                releaseDiscount={releaseDiscount}
                releaseCredit={releaseCredit}
                onReleaseDiscount={setReleaseDiscount}
                onReleaseCredit={setReleaseCredit}
              />
            )}
            {showPreview && seen && !fresh && (
              <p className="text-sm text-muted-foreground" role="status">
                Os dados mudaram depois da prévia: ao finalizar, a nota é conferida de novo.
              </p>
            )}
          </>
        )}
        {formError && <Alert tone="error">{formError}</Alert>}
        {stale ? (
          <Alert tone="error">
            O cálculo da nota mudou desde a conferência (preço, cadastro ou regra). Nada foi
            gravado: clique em Finalizar de novo para conferir com os dados atuais.
          </Alert>
        ) : (
          <SaveError error={finalize.error} />
        )}
      </StepWizard>
      <ConfirmDialog
        open={confirmCancel}
        title="Descartar esta venda?"
        confirmLabel="Descartar venda"
        cancelLabel="Continuar a venda"
        destructive
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => {
          setConfirmCancel(false);
          onCancel();
        }}
      >
        O que foi digitado será perdido. Nada foi gravado: nenhum pedido, estoque, título ou nota.
      </ConfirmDialog>
    </div>
  );
}

/** Resumo sempre visível ao lado das etapas. */
function SaleAside({
  customer,
  salesperson,
  payment,
  items,
  total,
  dirty,
}: {
  customer: string;
  salesperson: string;
  payment: string;
  items: number;
  total: string | null;
  dirty: boolean;
}) {
  return (
    <div aria-label="Resumo da venda" className="flex flex-col gap-3">
      <h2 className="text-base font-semibold">Resumo da venda</h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-sm">
        <dt className="text-muted-foreground">Cliente</dt>
        <dd className="text-right font-medium">{customer || "—"}</dd>
        <dt className="text-muted-foreground">Vendedor</dt>
        <dd className="text-right">{salesperson || "Do cadastro do cliente"}</dd>
        <dt className="text-muted-foreground">Pagamento</dt>
        <dd className="text-right">{payment}</dd>
        <dt className="text-muted-foreground">Itens</dt>
        <dd className="text-right">{items}</dd>
      </dl>
      <div className="flex items-baseline justify-between border-t border-border/70 pt-3">
        <span className="text-sm text-muted-foreground">Total</span>
        <span className="text-2xl font-bold tabular-nums">
          {total === null ? "—" : formatMoney(total)}
        </span>
      </div>
      <p className="text-[13px] text-muted-foreground">
        {dirty
          ? "Nada é gravado antes de Finalizar. Voltar às etapas não perde o que foi digitado."
          : "Comece pelo cliente."}
      </p>
    </div>
  );
}

/** Seção da revisão: ícone, título, atalho para corrigir e conteúdo. */
function ReviewSection({
  icon,
  title,
  action,
  children,
  className = "",
}: {
  icon: IconName;
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      aria-label={title}
      className={`min-w-0 rounded-xl border border-border/70 bg-card p-4 ${className}`}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold">
          <span
            aria-hidden
            className="flex size-8 items-center justify-center rounded-lg bg-caramel-soft text-accent"
          >
            <Icon name={icon} size={17} />
          </span>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/**
 * Etapa 4: tudo o que vai ser gravado, em quatro seções (dados, produtos, resumo financeiro,
 * emissão fiscal), cada uma com atalho para corrigir. O Total geral é o destaque.
 */
function SaleReview({
  customer,
  salesperson,
  payment,
  warehouseId,
  orderDate,
  items,
  preview,
  environment,
  prefs,
  onPrefs,
  onEdit,
}: {
  customer: string;
  salesperson: string;
  payment: string;
  warehouseId: string;
  orderDate: string;
  items: SaleDraftItem[];
  preview: {
    products: string | null;
    discounts: string;
    freight: string;
    other: string;
    total: string | null;
  };
  environment: FiscalEnvironment | null;
  prefs: AutoPrefs;
  onPrefs: (p: AutoPrefs) => void;
  onEdit: (step: number) => void;
}) {
  const warehouses = useWarehouses();
  const warehouse = warehouses.data?.find((w) => w.id === warehouseId);
  const editLink = (n: number, label: string) => (
    <Button size="sm" variant="ghost" onClick={() => onEdit(n)}>
      <Icon name="edit" size={15} />
      {label}
    </Button>
  );
  const money = (v: string | null) => (v === null ? "—" : formatMoney(v));
  const data: [string, string][] = [
    ["Cliente", customer || "—"],
    ["Vendedor", salesperson || "Do cadastro do cliente"],
    ["Forma de pagamento", payment],
    ["Depósito de saída", warehouse ? warehouse.name : "Padrão (fábrica)"],
    ["Data da venda", dateBR(orderDate)],
    ["Tabela de preços", "A do cliente (válida na data)"],
  ];
  return (
    <div aria-label="Revisão da venda" className="@container grid gap-4">
      <div className="grid gap-4 @3xl:grid-cols-[minmax(0,1fr)_minmax(0,20rem)]">
        <ReviewSection icon="vendas" title="Dados da venda" action={editLink(0, "Editar dados")}>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 @md:grid-cols-2">
            {data.map(([label, value]) => (
              <div key={label} className="min-w-0">
                <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
                <dd className="mt-0.5 text-sm font-medium break-words">{value}</dd>
              </div>
            ))}
          </dl>
        </ReviewSection>
        <ReviewSection
          icon="financeiro"
          title="Resumo financeiro"
          action={editLink(2, "Editar valores")}
        >
          <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 text-sm tabular-nums">
            <dt className="text-muted-foreground">Subtotal dos produtos (bruto)</dt>
            <dd className="text-right">{money(preview.products)}</dd>
            <dt className="text-muted-foreground">Descontos</dt>
            <dd className="text-right text-destructive">
              {Number(preview.discounts) === 0 ? "—" : `− ${formatMoney(preview.discounts)}`}
            </dd>
            <dt className="text-muted-foreground">Frete</dt>
            <dd className="text-right">{formatMoney(preview.freight)}</dd>
            <dt className="text-muted-foreground">Outras despesas</dt>
            <dd className="text-right">{formatMoney(preview.other)}</dd>
          </dl>
          <div className="mt-3 flex items-baseline justify-between gap-3 rounded-lg bg-caramel-soft px-4 py-3">
            <span className="text-sm font-semibold text-primary">Total geral</span>
            <span
              aria-label={`Total geral ${money(preview.total)}`}
              className="text-[28px] font-bold leading-none tabular-nums text-primary"
            >
              {money(preview.total)}
            </span>
          </div>
        </ReviewSection>
      </div>
      <ReviewSection
        icon="estoque"
        title={`Produtos (${items.length})`}
        action={editLink(1, "Editar produtos")}
      >
        <Table head={["Produto", "Un.", "Qtd.", "Preço unit.", "Desconto", "Total líquido"]}>
          {items.map((i) => {
            const price = i.unitPrice ?? i.listPrice;
            const line = saleLineTotal(i);
            return (
              <tr key={i.key}>
                <td className="px-3">
                  <span className="block font-mono text-xs text-muted-foreground">
                    {i.productCode}
                  </span>
                  {i.productName}
                </td>
                <td className="px-3">{i.unitCode}</td>
                <td className="px-3 text-right tabular-nums">{qtyText(i.quantity)}</td>
                <td className="px-3 text-right tabular-nums">{money(price)}</td>
                <td className="px-3 text-right tabular-nums text-destructive">
                  {Number(i.discount) === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    `− ${formatMoney(i.discount)}`
                  )}
                </td>
                <td className="px-3 text-right font-semibold tabular-nums">{money(line)}</td>
              </tr>
            );
          })}
        </Table>
      </ReviewSection>
      <ReviewSection icon="fiscal" title="Emissão fiscal">
        <div className="space-y-3 text-sm">
          <p className="flex items-start gap-2">
            <Icon name="info" size={18} className="mt-0.5 shrink-0 text-info" />
            <span>
              <strong>
                Ao finalizar, o sistema confirmará a venda e iniciará automaticamente a emissão da
                NF-e.
              </strong>{" "}
              A nota é conferida antes (as mesmas validações da prévia): com algum impedimento, nada
              é gravado e o motivo aparece aqui. A prévia fiscal é opcional.
            </span>
          </p>
          {environment && (
            <p>
              Ambiente:{" "}
              <Badge tone={environment === "producao" ? "ok" : "warn"}>
                {FISCAL_ENVIRONMENT_LABEL[environment]}
              </Badge>
            </p>
          )}
          <fieldset className="min-w-0 space-y-1">
            <legend className="mb-1 font-medium">Quando a SEFAZ autorizar</legend>
            <Checkbox
              label="Abrir o DANFE em nova guia"
              checked={prefs.openDanfe}
              onChange={(e) => onPrefs({ ...prefs, openDanfe: e.target.checked })}
            />
            <Checkbox
              label="Baixar o DANFE (PDF) e o XML autorizado, num único arquivo .zip"
              checked={prefs.download}
              onChange={(e) => onPrefs({ ...prefs, download: e.target.checked })}
            />
            <p className="text-[13px] text-muted-foreground">
              Um download só (o .zip), para o navegador não bloquear. A tela também mostra botões
              para abrir e baixar cada arquivo. A escolha fica guardada neste navegador.
            </p>
          </fieldset>
        </div>
      </ReviewSection>
    </div>
  );
}

function extraDetails(e: PaymentExtra, card: boolean, pix: boolean) {
  const v = (s: string) => (s.trim() === "" ? null : s.trim());
  if (card && (e.cardBrand || e.authorizationCode.trim() || e.nsu.trim())) {
    return {
      integrationType: "2" as const,
      cardBrand: v(e.cardBrand),
      authorizationCode: v(e.authorizationCode),
      nsu: v(e.nsu),
    };
  }
  if (pix && e.pixTxid.trim()) return { pixTxid: v(e.pixTxid) };
  return null;
}

function PreviewView({
  p,
  releaseDiscount,
  releaseCredit,
  onReleaseDiscount,
  onReleaseCredit,
}: {
  p: DirectSalePreview;
  releaseDiscount: boolean;
  releaseCredit: boolean;
  onReleaseDiscount: (v: boolean) => void;
  onReleaseCredit: (v: boolean) => void;
}) {
  const can = useCan();
  return (
    <div className="space-y-3 text-sm" aria-label="Prévia da Venda Direta">
      <p>
        Total da venda: <strong>{formatMoney(p.totals.totalAmount)}</strong>
        {p.totals.paymentMode === "titulo" ? " (a prazo: título a receber)" : " (à vista)"}
      </p>
      {p.shortages.length > 0 && (
        <Alert tone="warn">
          <strong>Venda sem estoque (permitida pelo seu perfil):</strong>{" "}
          {p.shortages
            .map(
              (s) =>
                `${s.productCode}: disponível ${qty(s.available, s.baseUnitCode)}, ficará ${qty(s.short, s.baseUnitCode)} a regularizar`,
            )
            .join("; ")}
          . A falta fica em Estoque → Pendências de estoque.
        </Alert>
      )}
      {p.needsDiscountRelease && (
        <div className="rounded-md border p-3">
          <p className="mb-2">Liberação comercial: {p.releaseDetails.join("; ")}.</p>
          <Checkbox
            label="Liberar preço / desconto (fica registrado em seu nome)"
            checked={releaseDiscount}
            onChange={(e) => onReleaseDiscount(e.target.checked)}
            disabled={!can("vendas.desconto.acima_limite")}
          />
        </div>
      )}
      {p.needsCreditRelease && (
        <div className="rounded-md border p-3">
          <Checkbox
            label="Liberar venda acima do limite de crédito do cliente"
            checked={releaseCredit}
            onChange={(e) => onReleaseCredit(e.target.checked)}
            disabled={!can("vendas.credito.liberar")}
          />
        </div>
      )}
      {(p.needsDiscountRelease || p.needsCreditRelease) && (
        <p className="text-muted-foreground">
          Depois de marcar a liberação, clique em Finalizar de novo (a nota é conferida outra vez).
        </p>
      )}
      {p.blockers.length > 0 && (
        <Alert tone="error">
          <strong>Não é possível finalizar:</strong>
          <ul className="mt-1 list-disc pl-5">
            {p.blockers.map((b, i) => (
              <li key={`${b.code}-${i}`}>{b.message}</li>
            ))}
          </ul>
        </Alert>
      )}
      {p.fiscal && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span>
              Nota: modelo {p.fiscal.calc.model ?? "—"} · operação{" "}
              {p.fiscal.calc.operation?.name ?? "—"}
              {p.fiscal.calc.operation?.nature ? ` (${p.fiscal.calc.operation.nature})` : ""}
            </span>
            <Badge tone={p.fiscal.environment === "producao" ? "ok" : "warn"}>
              Ambiente: {FISCAL_ENVIRONMENT_LABEL[p.fiscal.environment]}
            </Badge>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border p-3" aria-label="Emitente">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Emitente</p>
              <p className="font-medium">
                {p.fiscal.issuer.legalName}
                {p.fiscal.issuer.tradeName ? ` (${p.fiscal.issuer.tradeName})` : ""}
              </p>
              <p className="text-xs">
                CNPJ {formatDocument(p.fiscal.issuer.cnpj)} · IE{" "}
                {p.fiscal.issuer.stateRegistration ?? "—"} · CRT {p.fiscal.issuer.crt ?? "—"} ·{" "}
                {p.fiscal.issuer.city ?? "—"}/{p.fiscal.issuer.uf ?? "—"}
              </p>
            </div>
            <div className="rounded-md border p-3" aria-label="Destinatário">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Destinatário</p>
              <p className="font-medium">{p.fiscal.recipient.name}</p>
              <p className="text-xs">
                {p.fiscal.recipient.document
                  ? `CPF/CNPJ ${formatDocument(p.fiscal.recipient.document)} · `
                  : ""}
                {p.fiscal.recipient.ieIndicator === "1"
                  ? "contribuinte do ICMS"
                  : p.fiscal.recipient.ieIndicator === "2"
                    ? "contribuinte isento"
                    : "não contribuinte"}{" "}
                · UF {p.fiscal.recipient.uf ?? "—"}
              </p>
            </div>
          </div>
          <div>
            <h4 className="mb-1 font-semibold">Tributos por item</h4>
            <TaxBreakdown calc={p.fiscal.calc} />
          </div>
          <CalcView calc={p.fiscal.calc} />
        </div>
      )}
    </div>
  );
}
